import React from "react";
import { getImageUrl } from "utils/imageHelper";

export default function ViewOfferDetails({ data }) {
  if (!data) return null;

  const resolveImage = (imgPath) => {
    if (!imgPath) return null;
    return typeof imgPath === "string" ? getImageUrl(imgPath) : null;
  };

  // Helper to parse default_image metadata if formatted as JSON string
  const parseDefaultImage = (val) => {
    if (!val) return null;
    if (typeof val === "object") return val.path || val.value || null;
    try {
      const parsed = JSON.parse(val);
      return parsed.path || parsed.value || val;
    } catch {
      return val;
    }
  };

  const defaultImageUrl = resolveImage(parseDefaultImage(data.default_image));

  const galleryImages = Array.isArray(data.offerImages)
    ? data.offerImages.map(resolveImage).filter(Boolean)
    : Array.isArray(data.existingImages)
    ? data.existingImages.map(resolveImage).filter(Boolean)
    : [];

  const isActive =
    data.status?.toLowerCase() === "active" || data.status === true;

  const formatDateTime = (isoStr) => {
    if (!isoStr) return "N/A";
    try {
      const d = new Date(isoStr);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
        d.getUTCDate()
      )}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(
        d.getUTCSeconds()
      )}`;
    } catch {
      return isoStr;
    }
  };

  const targetProductsText = Array.isArray(data.target_products)
    ? data.target_products
        .map((p) => (typeof p === "string" ? p : p.product_name))
        .filter(Boolean)
        .join(", ")
    : "N/A";

  return (
    <div className="flex flex-col gap-6 text-sm text-gray-800">
      {/* SECTION 1: OFFER DETAILS */}
      <ViewSection title="Offer Details">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ViewDisplayBox label="Offer Title" value={data.title} />

          <ViewDisplayBox
            label="Exhibitor"
            value={data.exhibitor_id?.title || "N/A"}
          />

          <ViewDisplayBox
            label="Target Product(s)"
            value={targetProductsText}
            className="md:col-span-2"
          />

          <ViewDisplayBox label="Promo Code" value={data.promo_code} />

          <ViewDisplayBox label="Discount" value={data.discount} />

          <ViewDisplayBox
            label="Start Date"
            value={formatDateTime(data.start_date)}
          />

          <ViewDisplayBox
            label="End Date"
            value={formatDateTime(data.end_date)}
          />

          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="view-label">Description</span>
            <div
              className="view-value"
              dangerouslySetInnerHTML={{
                __html: data.description || "N/A",
              }}
            />
          </div>

          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="view-label">Terms & Conditions</span>
            <div className="view-value">{data.tc || "N/A"}</div>
          </div>
        </div>
      </ViewSection>

      {/* SECTION 2: STATUS */}
      <ViewSection title="Status">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="view-label">Status</span>
            <div className="view-value flex items-center">
              <span>{isActive ? "Active" : "Inactive"}</span>
            </div>
          </div>
        </div>
      </ViewSection>

      {/* SECTION 3: OFFER IMAGES */}
      <ViewSection title="Offer Images">
        <div className="flex flex-col gap-4">
          {/* Default Image */}
          <div className="flex flex-col gap-1">
            <span className="view-label">Default Image</span>
            <ImageContainer
              src={defaultImageUrl}
              alt="Default Offer Image"
              placeholder="No Default Image Set"
              height="h-36"
            />
          </div>

          {/* Gallery Images */}
          {galleryImages.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="view-label">Gallery Images</span>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {galleryImages.map((url, idx) => (
                  <ImageContainer
                    key={idx}
                    src={url}
                    alt={`Offer Image ${idx + 1}`}
                    placeholder="No Preview"
                    height="h-28"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </ViewSection>
    </div>
  );
}

function ViewSection({ title, children }) {
  return (
    <div className="view-card flex flex-col gap-6 text-sm">
      <div>
        <div className="view-subtitle">{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ViewDisplayBox({ label, value, className = "" }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className}`}>
      <span className="view-label">{label}</span>
      <div className="view-value">{value || "N/A"}</div>
    </div>
  );
}

function ImageContainer({
  src,
  alt,
  placeholder,
  height = "h-28",
}) {
  return (
    <div className={`${height} view-image-div`}>
      {src ? (
        <img
          src={src}
          alt={alt || "Media preview"}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <span className="text-xs text-gray-400">{placeholder}</span>
      )}
    </div>
  );
}